import { ButtonBS, Center, Text, VStack } from "@biblioteksentralen/react";
import { isNonNil, ResolvedBibliobattles } from "@libry-content/common";
import { ExternalLink } from "react-feather";
import { useClientSideReady } from "../../../utils/hooks/useClientSideReady";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { DefaultContainer } from "../../ContentContainer";
import { EventsList } from "../../events/EventsList";
import { Edit } from "../../editInSanity/EditInSanity";
import { ListsAndRecommendationsList } from "../../lists/ListsAndRecommendationsList";
import { useRelevantPublicationsHeading } from "../../lists/utils";
import { sortRecommendationBookListByDate } from "../../recommendation/utils";
import { CampaignViewSection } from "../CampaignViewSection";
import { useCampaignEvents } from "../useCampaignEvents";
import { BibliobattlesBanner } from "./BibliobattlesBanner";
import { BibliobattlesViewRichTextBlock } from "./BibliobattlesRichTextBlock";
import { BibliobattlesVideoStream } from "./BibliobattlesVideoStream";
import { BibliobattlesViewBanner } from "./BibliobattlesViewBanner";
import ItemGrid from "../../../searchAndCollection/components/works/ItemGrid";

type BibliobattlesViewProps = {
  bibliobattles: ResolvedBibliobattles | null;
};

export const BibliobattlesView = ({ bibliobattles }: BibliobattlesViewProps) => {
  const { t } = useTranslation();
  const clientSideReady = useClientSideReady();

  const bookLists = bibliobattles?.bookLists?.filter(isNonNil) ?? [];
  const recommendations = bibliobattles?.recommendations?.filter(isNonNil) ?? [];
  const hasListsOrRecommendations = !!bookLists.length || !!recommendations.length;
  const { events, showingAllEvents, toggleAllEvents, haveAdditionalEvents } = useCampaignEvents(bibliobattles?.events);
  const relevantPublicationsHeading = useRelevantPublicationsHeading(bibliobattles?.relevantPublications);

  if (!bibliobattles?.templateContent?.bannerBackgroundLandingPage?.asset) {
    return (
      <DefaultContainer margin={{ base: "0", lg: "auto" }}>
        {/* Banner shows information about missing content in non-production environments */}
        <BibliobattlesBanner bibliobattles={bibliobattles} />
      </DefaultContainer>
    );
  }

  const bibliobattlesUrl = bibliobattles.templateContent.url ? new URL(bibliobattles.templateContent.url) : undefined;
  const bibliobattlesHostname = bibliobattlesUrl?.hostname;

  return (
    <>
      <BibliobattlesViewBanner bibliobattles={bibliobattles} />
      <DefaultContainer
        margin={{ base: "0 0 3rem", sm: "0 auto 5rem", md: "0 auto 5rem" }}
        position="relative"
        padding={{ base: "0", md: "0 0.75rem" }}
      >
        <Edit
          doc={bibliobattles}
          label="Oppdater Bibliobattles-side"
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          top="-2rem"
        />
        <VStack gap={{ base: "3rem", sm: "4rem" }} style={{ hyphens: "none" }} marginTop="2.5rem">
          <CampaignViewSection fontSize="lg" maxWidth="38rem" alignItems="center" gap="1rem" className="group">
            <Text display="flex" flexDirection="column">
              <Text as="span" marginBottom="1rem">
                {t(
                  "Spiller du Minecraft? Er du under 18 år? Bli med på årets Bibliobattles-turnering! Kjemp  sammen med andre eller alene om å bli best i ditt fylke eller i hele landet."
                )}
              </Text>
              <Text as="span" marginBottom="1rem">
                {t(
                  "Konkurransen varer {datoer}. {antallKvelder} kvelder med bygging og live oppsummering på Twitch, YouTube og her.",
                  { datoer: "4. - 8. november", antallKvelder: 4 }
                )}
              </Text>
              <Text as="span" marginBottom="1rem">
                {t("Stor finale {sluttDato} med masse action og kule premier.", { sluttDato: "10. novemeber" })}
              </Text>
            </Text>
            {!!bibliobattlesUrl && (
              <ButtonBS as="a" href={bibliobattlesUrl.toString()} variant="secondary">
                {t("Les mer på")} {bibliobattlesHostname}
                <ExternalLink />
              </ButtonBS>
            )}
          </CampaignViewSection>

          {clientSideReady && <BibliobattlesVideoStream bibliobattles={bibliobattles} />}

          {!!events.length && (
            <CampaignViewSection headline={t("Bli med på våre Bibliobattles-arrangementer!")}>
              <EventsList events={events} />
              {haveAdditionalEvents && (
                <Center width="100%" zIndex="1">
                  <ButtonBS onClick={toggleAllEvents} variant="secondary">
                    {showingAllEvents ? t("Vis færre") : t("Vis alle")}
                  </ButtonBS>
                </Center>
              )}
            </CampaignViewSection>
          )}

          <BibliobattlesViewRichTextBlock bibliobattles={bibliobattles} />

          {hasListsOrRecommendations && (
            <CampaignViewSection headline={t("Pause fra spillinga?")}>
              <ListsAndRecommendationsList
                listsAndRecommendations={sortRecommendationBookListByDate([...bookLists, ...recommendations]) ?? []}
              />
            </CampaignViewSection>
          )}

          {!!bibliobattles.relevantPublications?.length && (
            <CampaignViewSection headline={relevantPublicationsHeading}>
              <ItemGrid items={bibliobattles.relevantPublications} />
            </CampaignViewSection>
          )}
        </VStack>
      </DefaultContainer>
    </>
  );
};
